import toast from 'react-hot-toast'
import { keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/react-query'

import { Indicator, invalidateIndicator } from '@shared/hooks/src/useIndicator'
import useQuery from '@shared/hooks/src/useQuery'
import { useMe } from '@shared/providers/src/MeProvider'
import { queryClient, setQueriesData, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { filterCache, flatten, handleError, mapCache, pageParam, QK } from '@shared/utils'

export function useAssessmentDetails(assessmentId, options = {}) {
  return useQuery({
    queryKey: QK.assessments.id(assessmentId).details,
    queryFn: () => API.assessments.id(assessmentId).details(),
    select: (assessment) => ({ ...assessment, answers: JSON.parse(assessment.answers) || [] }),
    ...options,
  })
}

export function useAssessmentReview(patientId, encounterId, assessmentId) {
  const me = useMe()

  return useMutation({
    mutationFn: () => API.patients.id(patientId).encounters.id(encounterId).assessments.id(assessmentId).review(),
    onSuccess: (assessment) => {
      toast.success('Assessment reviewed successfully')

      setQueryData(QK.assessments.id(assessment.id).details, (old) => ({ ...old, ...assessment }))

      setQueriesData(
        { queryKey: QK.patients.id(patientId).encounters.id(encounterId).assessments.lists },
        mapCache((asmt) => (asmt.id === assessment.id ? { ...asmt, ...assessment } : asmt))
      )

      setQueriesData(
        { queryKey: QK.assessments.pending.lists },
        filterCache((asmt) => asmt.id !== assessment.id)
      )

      invalidateIndicator(me.id, Indicator.PendingAssessments)

      queryClient.invalidateQueries({ queryKey: QK.scheduleSlots.providers.scheduled.ooo.lists })
    },
  })
}

export function useAssessmentMarkExceptional(assessmentId, providerId) {
  const me = useMe()

  return useMutation({
    mutationFn: ({ value, reason }) =>
      value ? API.assessments.id(assessmentId).markExceptional({ reason }) : API.assessments.id(assessmentId).unmarkExceptional(),
    onSuccess: (assessment, { value }) => {
      const mergeAssessments = (oldAsmt, newAsmt) => ({
        ...oldAsmt,
        exceptional: newAsmt.exceptional,
        exceptionalById: newAsmt.exceptionalById,
      })

      const updater = mapCache((old) => {
        return old.id === assessment.id ? mergeAssessments(old, assessment) : old
      })

      setQueryData(QK.assessments.id(assessment.id).details, (old) => mergeAssessments(old, assessment))

      if (providerId) {
        setQueriesData({ queryKey: QK.providers.id(providerId).dashboard.assessments.lists }, updater)
      }

      if (assessment.user && assessment.encounterId) {
        setQueriesData({ queryKey: QK.patients.id(assessment.user.id).encounters.id(assessment.encounterId).assessments.lists }, updater)
      }

      setQueriesData({ queryKey: QK.assessments.pending.lists }, updater)

      if (value) {
        setQueriesData(
          { queryKey: QK.assessments.pending.lists },
          filterCache((a) => a.id !== assessmentId)
        )
      }

      queryClient.invalidateQueries({ queryKey: QK.assessments.pending.lists })
      queryClient.invalidateQueries({ queryKey: QK.assessments.id(assessmentId).notes.lists })

      invalidateIndicator(me.id, Indicator.PendingAssessments)

      queryClient.invalidateQueries({ queryKey: QK.scheduleSlots.providers.scheduled.ooo.lists })
    },
  })
}

const NOTES_LIMIT = 5

export function useAssessmentNotes(assessmentId) {
  const query = { limit: NOTES_LIMIT }
  return useInfiniteQuery({
    queryKey: QK.assessments.id(assessmentId).notes.list(query),
    queryFn: ({ pageParam }) => API.assessments.id(assessmentId).notes.list({ ...query, offset: pageParam * NOTES_LIMIT }),
    placeholderData: keepPreviousData,
    enabled: Boolean(assessmentId),
    select: flatten,
    initialPageParam: 0,
    getNextPageParam: pageParam(NOTES_LIMIT),
  })
}

export function useAssessmentNoteCreation(assessmentId, onCreate) {
  return useMutation({
    mutationFn: (message) => API.assessments.id(assessmentId).notes.create({ message }),
    onSuccess: () => {
      onCreate?.()
      return queryClient.invalidateQueries({ queryKey: QK.assessments.id(assessmentId).notes.lists })
    },
    onError: handleError,
  })
}

export const invalidateEncounterNotes = (assessment) => {
  if (!assessment) return

  const patientId = assessment?.userId
  const encounterId = assessment?.encounterId

  if (!patientId || !encounterId) return

  queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).encounters.id(encounterId).notes.lists })
}
